import type { ExperimentDefinition } from '../domain/abTesting.types';

// FNV-1a 32-bit hash (fast + deterministic, good enough for bucketing)
function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // hash *= 16777619 (with 32-bit overflow)
    hash = Math.imul(hash, 0x01000193);
  }
  // force uint32
  return hash >>> 0;
}

function pickWeightedVariant<TVariant extends string>(
  variants: readonly { id: TVariant; weight: number }[],
  bucket0to9999: number
): TVariant {
  const cleaned = variants
    .map(v => ({ id: v.id, weight: Number.isFinite(v.weight) ? Math.max(0, v.weight) : 0 }))
    .filter(v => v.weight > 0);

  if (cleaned.length === 0) {
    // fallback: if everything is invalid, return the first variant id (even if weight is 0)
    return variants[0]?.id as TVariant;
  }

  const total = cleaned.reduce((sum, v) => sum + v.weight, 0);
  let cumulative = 0;
  for (const v of cleaned) {
    cumulative += v.weight;
    const threshold = Math.floor((cumulative / total) * 10000); // 0..10000
    if (bucket0to9999 < threshold) return v.id;
  }
  return cleaned[cleaned.length - 1].id;
}

export function resolveVariantForExperiment<TVariant extends string>(
  experimentKey: string,
  experiment: ExperimentDefinition<TVariant> | undefined,
  abUid: string | null
): TVariant {
  if (!experiment || !experiment.variants?.length) {
    return 'A' as TVariant;
  }

  // If we don't have a stable bucketing key (visitorId), always show the default variant.
  if (!abUid) {
    return experiment.defaultVariant ?? experiment.variants[0].id;
  }

  const bucket = fnv1a32(`${abUid}::${experimentKey}`) % 10000;
  const picked = pickWeightedVariant(experiment.variants, bucket);
  return picked ?? (experiment.defaultVariant ?? experiment.variants[0].id);
}

export type JsonWeightedVariants = Record<
  string,
  { weight?: number; percent?: number } & Record<string, unknown>
>;

function stripWeightFields<T extends Record<string, unknown>>(variant: T): T {
  // Avoid leaking config fields (weight/percent) into component props.
  // Keep everything else as-is.
  const { weight: _w, percent: _p, ...rest } = variant as unknown as {
    weight?: unknown;
    percent?: unknown;
  } & Record<string, unknown>;
  return rest as T;
}

/**
 * Resolve and merge a JSON-defined variant into a base section object.
 *
 * Expected shape:
 * - base.variants = { A: { weight, ...data }, B: { weight, ...data }, ... }
 *
 * Weights can be provided via `weight` or `percent` per variant.
 */
export function resolveAndMergeABTestingVariant<TBase extends Record<string, unknown>>(args: {
  experimentKey: string;
  base: TBase;
  abUid: string | null;
  variantsField?: string;
}): { variantKey: string; resolved: TBase } {
  const variantsField = args.variantsField ?? 'variants';
  const variants = args.base[variantsField] as JsonWeightedVariants | undefined;

  if (!variants || typeof variants !== 'object') {
    return { variantKey: 'A', resolved: args.base };
  }

  const defs = Object.entries(variants).map(([id, v]) => {
    const raw = v.weight ?? v.percent;
    const weight = Number.isFinite(Number(raw)) ? Number(raw) : 1;
    return { id, weight };
  });

  const defaultVariant = (variants.A ? 'A' : Object.keys(variants)[0]) || 'A';
  const variantKey = resolveVariantForExperiment(
    args.experimentKey,
    { variants: defs, defaultVariant },
    args.abUid
  );

  const picked = (variants[variantKey] || variants.A || variants.B || variants[defaultVariant]) as
    | Record<string, unknown>
    | undefined;

  if (!picked) return { variantKey, resolved: args.base };

  const merged = { ...args.base, ...stripWeightFields(picked) } as TBase;
  return { variantKey, resolved: merged };
}

export function getCookieValue(cookieHeader: string | undefined, name: string): string | null {
  if (!cookieHeader) return null;
  const parts = cookieHeader.split(/;\s*/g);
  for (const part of parts) {
    const idx = part.indexOf('=');
    if (idx === -1) continue;
    const k = part.slice(0, idx).trim();
    if (k !== name) continue;
    const v = part.slice(idx + 1);
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  }
  return null;
}

